import { isPageVisible, onVisibilityChange } from "./pageVisibility";

const PROBE_URL =
  process.env.REACT_APP_CONNECTIVITY_PROBE_URL ||
  (process.env.REACT_APP_publicUrl ? `${process.env.REACT_APP_publicUrl}/favicon.ico` : "");
const PROBE_TIMEOUT = 8000;
const HEALTHY_INTERVAL = 30000;
const RETRY_INTERVAL = 3000;
const OFFLINE_INTERVAL = 5000;
const FAILURES_BEFORE_OFFLINE = 2;

const listeners = new Set();

let status = "online";
let failures = 0;
let timerId = null;
let inFlight = false;
let unsubscribeVisibility = null;
let started = false;

const broadcast = () => {
  listeners.forEach((listener) => {
    try {
      listener(status);
    } catch (error) {
      console.error("connectionStatus", error);
    }
  });
};

const setStatus = (next) => {
  if (status === next) return;
  status = next;
  broadcast();
};

const debug = (event) => {
  window.__wesConnection = {
    event,
    status,
    failures,
    probeUrl: PROBE_URL,
    onLine: navigator.onLine,
    visible: isPageVisible(),
    at: new Date().toLocaleTimeString(),
  };
};

const probe = async () => {
  if (!PROBE_URL) return true;
  const controller = new AbortController();
  const timeout = setTimeout(() => controller.abort(), PROBE_TIMEOUT);
  try {
    await fetch(`${PROBE_URL}?_=${Date.now()}`, {
      method: "HEAD",
      mode: "no-cors",
      cache: "no-store",
      signal: controller.signal,
    });
    return true;
  } catch (error) {
    return false;
  } finally {
    clearTimeout(timeout);
  }
};

const nextDelay = () => {
  if (status === "offline") return OFFLINE_INTERVAL;
  return failures > 0 ? RETRY_INTERVAL : HEALTHY_INTERVAL;
};

const schedule = () => {
  clearTimeout(timerId);
  if (!started || !isPageVisible()) return;
  timerId = setTimeout(runCheck, nextDelay());
};

const runCheck = async () => {
  if (inFlight) return;
  if (!navigator.onLine) {
    failures = FAILURES_BEFORE_OFFLINE;
    setStatus("offline");
    schedule();
    return;
  }

  inFlight = true;
  const reachable = await probe();
  inFlight = false;

  if (!navigator.onLine) {
    failures = FAILURES_BEFORE_OFFLINE;
    setStatus("offline");
    debug("offline-during-probe");
  } else if (reachable) {
    failures = 0;
    setStatus("online");
    debug("probe-ok");
  } else {
    failures += 1;
    if (failures >= FAILURES_BEFORE_OFFLINE) setStatus("offline");
    debug("probe-failed");
  }
  schedule();
};

const handleOffline = () => {
  failures = FAILURES_BEFORE_OFFLINE;
  setStatus("offline");
  debug("offline-event");
  schedule();
};

const handleOnline = () => {
  failures = 0;
  debug("online-event");
  runCheck();
};

const handleVisibility = (visible) => {
  if (visible) {
    runCheck();
  } else {
    clearTimeout(timerId);
  }
};

const start = () => {
  if (started || typeof window === "undefined") return;
  started = true;
  status = navigator.onLine ? "online" : "offline";
  failures = status === "offline" ? FAILURES_BEFORE_OFFLINE : 0;
  window.addEventListener("online", handleOnline);
  window.addEventListener("offline", handleOffline);
  unsubscribeVisibility = onVisibilityChange(handleVisibility);
  debug("start");
  runCheck();
};

const stop = () => {
  if (!started) return;
  started = false;
  clearTimeout(timerId);
  timerId = null;
  window.removeEventListener("online", handleOnline);
  window.removeEventListener("offline", handleOffline);
  if (unsubscribeVisibility) unsubscribeVisibility();
  unsubscribeVisibility = null;
};

export const getConnectionStatus = () => status;

export const subscribeConnection = (listener) => {
  start();
  listeners.add(listener);
  return () => {
    listeners.delete(listener);
    if (listeners.size === 0) stop();
  };
};
